import { connectionManager } from '../supabase/connectionManager';
import {
  MatchplayTournament,
  MatchplayPlayer,
  MatchplayMatch,
  MatchplayHole,
  MatchplayEvent,
  MatchplayTournamentRow,
  MatchplayPlayerRow,
  MatchplayMatchRow,
  MatchplayHoleRow,
  MatchplayEventRow,
  CreateTournamentRequest,
  CreatePlayerRequest,
  UpdateHoleRequest,
  MatchplayFormat,
  MatchStatus
} from '../../types/matchplay-v2';

export class MatchplayDatabase {
  private static getClient() {
    const supabase = connectionManager.getClient();
    if (!supabase) {
      throw new Error('Supabase client not available');
    }
    return supabase;
  }

  // Tournament Operations
  static async createTournament(request: CreateTournamentRequest): Promise<MatchplayTournament> {
    const supabase = this.getClient();
    
    const tournamentData = {
      name: request.name,
      format: request.format,
      course_id: request.courseId,
      start_date: request.startDate,
      end_date: request.endDate,
      status: 'setup' as const,
      metadata: {
        description: request.description,
        totalMatches: 0,
        completedMatches: 0
      }
    };

    console.log('Creating tournament with data:', tournamentData);

    const { data, error } = await supabase
      .from('matchplay_tournaments')
      .insert(tournamentData)
      .select()
      .single();

    if (error) {
      console.error('Error creating tournament:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      console.error('Tournament data that failed:', JSON.stringify(tournamentData, null, 2));
      throw new Error(`Failed to create tournament: ${error.message}`);
    }
    
    return this.mapTournamentRow(data);
  }

  static async getTournament(id: string): Promise<MatchplayTournament | null> {
    const supabase = this.getClient();
    
    try {
      const { data, error } = await supabase
        .from('matchplay_tournaments')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null; // Not found
        console.error('Error fetching tournament:', error);
        throw new Error(`Failed to fetch tournament: ${error.message}`);
      }
      
      return this.mapTournamentRow(data);
    } catch (error) {
      console.error('Database error in getTournament:', error);
      // If the error might be due to missing course_id column, try without it
      if (error instanceof Error && error.message.includes('course_id')) {
        console.warn('Attempting fallback query for single tournament without course_id column');
        return this.getTournamentLegacy(id);
      }
      throw error;
    }
  }

  // Fallback method for single tournament without course_id column
  private static async getTournamentLegacy(id: string): Promise<MatchplayTournament | null> {
    const supabase = this.getClient();
    
    const { data, error } = await supabase
      .from('matchplay_tournaments')
      .select('id, name, format, start_date, end_date, status, metadata, created_at, updated_at')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      console.error('Error in legacy tournament fetch:', error);
      throw new Error(`Failed to fetch tournament (legacy): ${error.message}`);
    }
    
    return this.mapTournamentRowLegacy(data);
  }

  static async getTournaments(): Promise<MatchplayTournament[]> {
    const supabase = this.getClient();
    
    try {
      const { data, error } = await supabase
        .from('matchplay_tournaments')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching tournaments:', error);
        throw new Error(`Failed to fetch tournaments: ${error.message}`);
      }
      
      return data.map(this.mapTournamentRow);
    } catch (error) {
      console.error('Database error in getTournaments:', error);
      // If the error might be due to missing course_id column, try without it
      if (error instanceof Error && error.message.includes('course_id')) {
        console.warn('Attempting fallback query without course_id column');
        return this.getTournamentsLegacy();
      }
      throw error;
    }
  }

  // Fallback method for databases without course_id column
  private static async getTournamentsLegacy(): Promise<MatchplayTournament[]> {
    const supabase = this.getClient();
    
    const { data, error } = await supabase
      .from('matchplay_tournaments')
      .select('id, name, format, start_date, end_date, status, metadata, created_at, updated_at')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error in legacy tournament fetch:', error);
      throw new Error(`Failed to fetch tournaments (legacy): ${error.message}`);
    }
    
    return data.map(row => this.mapTournamentRowLegacy(row));
  }

  // Legacy mapping for tournaments without course_id
  private static mapTournamentRowLegacy(row: any): MatchplayTournament {
    return {
      id: row.id,
      name: row.name,
      format: row.format as MatchplayFormat,
      courseId: undefined, // No course_id in legacy data
      startDate: row.start_date,
      endDate: row.end_date,
      status: row.status as 'setup' | 'active' | 'completed',
      metadata: row.metadata || {},
      createdAt: row.created_at,
      updatedAt: row.updated_at
    };
  }

  static async updateTournamentStatus(id: string, status: 'setup' | 'active' | 'completed'): Promise<void> {
    const supabase = this.getClient();
    
    const { error } = await supabase
      .from('matchplay_tournaments')
      .update({ 
        status,
        updated_at: new Date().toISOString()
      })
      .eq('id', id);

    if (error) throw error;
  }

  static async updateTournamentFormat(id: string, format: MatchplayFormat): Promise<void> {
    const supabase = this.getClient();
    
    const { error } = await supabase
      .from('matchplay_tournaments')
      .update({ 
        format,
        updated_at: new Date().toISOString()
      })
      .eq('id', id);

    if (error) throw error;
  }

  // Player Operations
  static async addPlayers(tournamentId: string, players: CreatePlayerRequest[]): Promise<MatchplayPlayer[]> {
    const supabase = this.getClient();
    
    const playerData = players.map((player, index) => ({
      tournament_id: tournamentId,
      player_name: player.playerName,
      handicap: player.handicap,
      course_handicap: Math.round(player.handicap), // Simplified for now
      team_assignment: player.teamAssignment,
      match_number: player.matchNumber,
      pairing_group: player.matchNumber || Math.floor(index / 2) + 1 // Use matchNumber or calculate from index
    }));

    const { data, error } = await supabase
      .from('matchplay_players')
      .insert(playerData)
      .select();

    if (error) throw error;
    return data.map(this.mapPlayerRow);
  }

  static async getPlayers(tournamentId: string): Promise<MatchplayPlayer[]> {
    const supabase = this.getClient();
    
    const { data, error } = await supabase
      .from('matchplay_players')
      .select('*')
      .eq('tournament_id', tournamentId)
      .order('pairing_group', { ascending: true })
      .order('match_number', { ascending: true })
      .order('team_assignment', { ascending: true })
      .order('player_name', { ascending: true }); // Fallback for consistent ordering

    if (error) throw error;
    return data.map(this.mapPlayerRow);
  }

  static async updatePlayerTeamAssignment(playerId: string, teamAssignment: 'A' | 'B' | null): Promise<void> {
    const supabase = this.getClient();
    
    const { error } = await supabase
      .from('matchplay_players')
      .update({ team_assignment: teamAssignment })
      .eq('id', playerId);

    if (error) throw error;
  }

  // Match Operations
  static async generateMatches(tournamentId: string, format: MatchplayFormat): Promise<MatchplayMatch[]> {
    const players = await this.getPlayers(tournamentId);
    const matches: any[] = [];

    if (format === 'singles') {
      // Generate singles matches
      for (let i = 0; i < players.length; i += 2) {
        if (i + 1 < players.length) {
          matches.push({
            tournament_id: tournamentId,
            match_number: Math.floor(i / 2) + 1,
            format: format, // Ensure we use the actual format parameter
            team1_players: [players[i].id],
            team2_players: [players[i + 1].id],
            status: 'pending',
            starting_hole: 1
          });
        }
      }
    } else {
      // Generate team matches (fourball/foursomes)
      const teamA = players.filter(p => p.teamAssignment === 'A');
      const teamB = players.filter(p => p.teamAssignment === 'B');
      
      if (teamA.length === 0 || teamB.length === 0) {
        throw new Error(`Insufficient players for team format. Team A: ${teamA.length}, Team B: ${teamB.length}`);
      }
      
      // Check if players have match numbers assigned
      const hasMatchNumbers = teamA.some(p => p.matchNumber) || teamB.some(p => p.matchNumber);
      
      if (hasMatchNumbers) {
        // Use match numbers to group players (same logic as preview)
        const matchNumbers = Array.from(new Set([
          ...teamA.filter(p => p.matchNumber).map(p => p.matchNumber!),
          ...teamB.filter(p => p.matchNumber).map(p => p.matchNumber!)
        ])).sort((a, b) => a - b);
        
        matchNumbers.forEach(matchNum => {
          const teamAPlayers = teamA.filter(p => p.matchNumber === matchNum);
          const teamBPlayers = teamB.filter(p => p.matchNumber === matchNum);
          
          if (teamAPlayers.length >= 2 && teamBPlayers.length >= 2) {
            matches.push({
              tournament_id: tournamentId,
              match_number: matchNum,
              format: format,
              team1_players: [teamAPlayers[0].id, teamAPlayers[1].id],
              team2_players: [teamBPlayers[0].id, teamBPlayers[1].id],
              status: 'pending',
              starting_hole: 1
            });
          }
        });
      } else {
        // Fallback to sequential pairing if no match numbers
        for (let i = 0; i < Math.min(teamA.length, teamB.length); i += 2) {
          if (i + 1 < teamA.length && i + 1 < teamB.length) {
            matches.push({
              tournament_id: tournamentId,
              match_number: Math.floor(i / 2) + 1,
              format: format,
              team1_players: [teamA[i].id, teamA[i + 1].id],
              team2_players: [teamB[i].id, teamB[i + 1].id],
              status: 'pending',
              starting_hole: 1
            });
          }
        }
      }
    }

    const supabase = this.getClient();
    
    console.log('🎯 Generating matches for tournament:', tournamentId);
    console.log('📊 Match data preview:', matches.map(m => ({
      match_number: m.match_number,
      format: m.format,
      team1_count: m.team1_players.length,
      team2_count: m.team2_players.length
    })));
    
    const { data, error } = await supabase
      .from('matchplay_matches')
      .insert(matches)
      .select();

    if (error) {
      console.error('❌ Match generation failed:', error);
      throw error;
    }
    
    console.log('✅ Successfully generated', data.length, 'matches');

    // Update tournament metadata
    await supabase
      .from('matchplay_tournaments')
      .update({ 
        metadata: { totalMatches: matches.length },
        updated_at: new Date().toISOString()
      })
      .eq('id', tournamentId);

    return data.map(row => this.mapMatchRow(row, []));
  }

  static async getMatches(tournamentId: string): Promise<MatchplayMatch[]> {
    const supabase = this.getClient();
    
    const { data: matchData, error: matchError } = await supabase
      .from('matchplay_matches')
      .select('*')
      .eq('tournament_id', tournamentId)
      .order('match_number');

    if (matchError) throw matchError;

    // Get all holes for these matches
    const matchIds = matchData.map(m => m.id);
    const { data: holeData, error: holeError } = await supabase
      .from('matchplay_holes')
      .select('*')
      .in('match_id', matchIds)
      .order('hole_number');

    if (holeError) throw holeError;

    // Group holes by match
    const holesByMatch = holeData.reduce((acc, hole) => {
      if (!acc[hole.match_id]) acc[hole.match_id] = [];
      acc[hole.match_id].push(this.mapHoleRow(hole));
      return acc;
    }, {} as Record<string, MatchplayHole[]>);

    return matchData.map(match => this.mapMatchRow(match, holesByMatch[match.id] || []));
  }

  static async getMatch(matchId: string): Promise<MatchplayMatch | null> {
    const supabase = this.getClient();
    
    const { data: matchData, error: matchError } = await supabase
      .from('matchplay_matches')
      .select('*')
      .eq('id', matchId)
      .single();

    if (matchError) {
      if (matchError.code === 'PGRST116') return null;
      throw matchError;
    }

    const { data: holeData, error: holeError } = await supabase
      .from('matchplay_holes')
      .select('*')
      .eq('match_id', matchId)
      .order('hole_number');

    if (holeError) throw holeError;

    const holes = holeData.map(this.mapHoleRow);
    return this.mapMatchRow(matchData, holes);
  }

  // Hole Operations
  static async updateHole(matchId: string, request: UpdateHoleRequest): Promise<MatchplayHole> {
    const supabase = this.getClient();
    
    // Calculate playing scores based on format
    const match = await this.getMatch(matchId);
    if (!match) throw new Error('Match not found');

    // Safety checks for score arrays
    if (!Array.isArray(request.team1GrossScores) || !Array.isArray(request.team2GrossScores)) {
      throw new Error('Invalid score data: team scores must be arrays');
    }

    if (request.team1GrossScores.length === 0 || request.team2GrossScores.length === 0) {
      throw new Error('Invalid score data: team scores cannot be empty');
    }

    let team1PlayingScore: number;
    let team2PlayingScore: number;

    if (match.format === 'singles') {
      team1PlayingScore = request.team1GrossScores[0];
      team2PlayingScore = request.team2GrossScores[0];
    } else if (match.format === 'fourball') {
      // Best ball - lowest score (filter out 0 scores)
      const team1ValidScores = request.team1GrossScores.filter(score => score > 0);
      const team2ValidScores = request.team2GrossScores.filter(score => score > 0);
      
      if (team1ValidScores.length === 0 || team2ValidScores.length === 0) {
        throw new Error('Invalid score data: at least one valid score per team required');
      }
      
      team1PlayingScore = Math.min(...team1ValidScores);
      team2PlayingScore = Math.min(...team2ValidScores);
    } else {
      // Foursomes - single team score
      team1PlayingScore = request.team1GrossScores[0];
      team2PlayingScore = request.team2GrossScores[0];
    }

    // Determine hole winner
    let holeWinner: 'team1' | 'team2' | 'halved';
    if (team1PlayingScore < team2PlayingScore) {
      holeWinner = 'team1';
    } else if (team2PlayingScore < team1PlayingScore) {
      holeWinner = 'team2';
    } else {
      holeWinner = 'halved';
    }

    const holeData = {
      match_id: matchId,
      hole_number: request.holeNumber,
      team1_gross_scores: request.team1GrossScores,
      team2_gross_scores: request.team2GrossScores,
      team1_playing_score: team1PlayingScore,
      team2_playing_score: team2PlayingScore,
      hole_winner: holeWinner,
      strokes_applied: request.strokesApplied || {}
    };

    const { data, error } = await supabase
      .from('matchplay_holes')
      .upsert(holeData, { onConflict: 'match_id,hole_number' })
      .select()
      .single();

    if (error) throw error;

    // Check if match should be completed
    await this.checkMatchCompletion(matchId);

    return this.mapHoleRow(data);
  }

  static async deleteHole(matchId: string, holeNumber: number): Promise<void> {
    const supabase = this.getClient();
    
    const { error } = await supabase
      .from('matchplay_holes')
      .delete()
      .eq('match_id', matchId)
      .eq('hole_number', holeNumber);

    if (error) throw error;
  }

  // Match completion logic
  private static async checkMatchCompletion(matchId: string): Promise<void> {
    const match = await this.getMatch(matchId);
    if (!match) return;

    const team1Wins = match.holes.filter(h => h.holeWinner === 'team1').length;
    const team2Wins = match.holes.filter(h => h.holeWinner === 'team2').length;
    const holesPlayed = match.holes.length;
    const holesRemaining = 18 - holesPlayed;
    const lead = Math.abs(team1Wins - team2Wins);

    let newStatus = match.status;
    let result: string | undefined;
    let winner: 'team1' | 'team2' | 'tie' | undefined;

    // Check for completion conditions
    if (holesPlayed === 18) {
      // All holes played
      newStatus = 'completed';
      if (team1Wins > team2Wins) {
        winner = 'team1';
        result = 'AS';
      } else if (team2Wins > team1Wins) {
        winner = 'team2';
        result = 'AS';
      } else {
        winner = 'tie';
        result = 'AS';
      }
    } else if (lead > holesRemaining) {
      // Mathematically impossible to catch up
      newStatus = 'completed';
      winner = team1Wins > team2Wins ? 'team1' : 'team2';
      result = `${lead}&${holesRemaining}`;
    } else if (lead === holesRemaining && holesRemaining > 0) {
      // Dormie situation
      newStatus = 'dormie';
    }

    if (newStatus !== match.status) {
      const supabase = this.getClient();
      await supabase
        .from('matchplay_matches')
        .update({
          status: newStatus,
          result,
          winner,
          updated_at: new Date().toISOString()
        })
        .eq('id', matchId);
    }
  }

  // Event logging
  static async logEvent(matchId: string, eventType: string, eventData: any): Promise<void> {
    const supabase = this.getClient();
    
    const { error } = await supabase
      .from('matchplay_events')
      .insert({
        match_id: matchId,
        event_type: eventType,
        event_data: eventData
      });

    if (error) throw error;
  }

  // Mapping functions
  private static mapTournamentRow(row: MatchplayTournamentRow): MatchplayTournament {
    return {
      id: row.id,
      name: row.name,
      format: row.format as MatchplayFormat,
      courseId: row.course_id,
      startDate: row.start_date,
      endDate: row.end_date,
      status: row.status as 'setup' | 'active' | 'completed',
      metadata: row.metadata || {},
      createdAt: row.created_at,
      updatedAt: row.updated_at
    };
  }

  private static mapPlayerRow(row: MatchplayPlayerRow): MatchplayPlayer {
    return {
      id: row.id,
      tournamentId: row.tournament_id,
      playerName: row.player_name,
      handicap: row.handicap,
      courseHandicap: row.course_handicap,
      teamAssignment: row.team_assignment as 'A' | 'B' | undefined,
      pairingGroup: row.pairing_group,
      matchNumber: row.match_number,
      createdAt: row.created_at
    };
  }

  private static mapMatchRow(row: MatchplayMatchRow, holes: MatchplayHole[]): MatchplayMatch {
    return {
      id: row.id,
      tournamentId: row.tournament_id,
      matchNumber: row.match_number,
      format: row.format as MatchplayFormat,
      team1Players: row.team1_players || [],
      team2Players: row.team2_players || [],
      status: row.status as MatchStatus,
      result: row.result,
      winner: row.winner as 'team1' | 'team2' | 'tie' | undefined,
      startingHole: row.starting_hole,
      holes,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    };
  }

  private static mapHoleRow(row: MatchplayHoleRow): MatchplayHole {
    return {
      id: row.id,
      matchId: row.match_id,
      holeNumber: row.hole_number,
      team1GrossScores: row.team1_gross_scores,
      team2GrossScores: row.team2_gross_scores,
      team1NetScores: row.team1_net_scores,
      team2NetScores: row.team2_net_scores,
      team1PlayingScore: row.team1_playing_score,
      team2PlayingScore: row.team2_playing_score,
      holeWinner: row.hole_winner as 'team1' | 'team2' | 'halved',
      strokesApplied: row.strokes_applied || {},
      createdAt: row.created_at
    };
  }
} 